/**
 * Stitch Review entry-point widgets — issue #818.
 *
 * Two presentations of the same underlying count:
 *
 *   - `StitchReviewCard` — full card for ProjectPage.tsx's Explore
 *     section, matches the Database Schema / API Endpoints cards.
 *   - `StitchReviewBadge` — compact button for E2EFlowsPage.tsx's
 *     catalog toolbar, sits next to the legacy "API calls need
 *     review" button from issue #746.
 *
 * Both fetch the review queue lazily, fail silently (so a stitcher
 * backend error never breaks the host page), and render nothing when
 * the project has no analysis yet.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';

interface StitchCounts {
  unresolved: number;
  ambiguous: number;
  state: 'ready' | 'no-analysis' | 'no-call-graphs';
}

function useStitchCounts(projectId: string | undefined): StitchCounts | null {
  const [counts, setCounts] = useState<StitchCounts | null>(null);
  useEffect(() => {
    if (!projectId) {
      setCounts(null);
      return;
    }
    let cancelled = false;
    api
      .getStitchReview(projectId)
      .then(data => {
        if (cancelled) return;
        // Defensive defaults: the entry-point widget must never crash
        // the host page if the backend drifts its response shape. A
        // missing array is treated as an empty one.
        setCounts({
          unresolved: data.unresolved?.length ?? 0,
          ambiguous: data.ambiguousCandidates?.length ?? 0,
          state: data.state,
        });
      })
      .catch(() => {
        // Swallow — the entry-point widget is best-effort and should
        // never block the host page when the stitcher pipeline is
        // unavailable.
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  return counts;
}

export function StitchReviewCard({ projectId }: { projectId: string | undefined }) {
  const counts = useStitchCounts(projectId);
  if (!projectId) return null;

  // Keep the card present even when the state is no-call-graphs or
  // no-analysis so the user can click in and see the explanation.
  const totalToReview = counts ? counts.unresolved + counts.ambiguous : null;
  const hasWork = totalToReview != null && totalToReview > 0;

  return (
    <Link
      to={`/projects/${projectId}/stitch-review`}
      className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-2 mb-2">
        <svg
          className="w-5 h-5 text-amber-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
        <h3 className="font-semibold group-hover/card:text-primary transition-colors">
          Stitch Review
        </h3>
        {hasWork && (
          <span className="ml-auto inline-block px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-full">
            {totalToReview}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        {counts == null
          ? 'Resolve API calls the auto-stitcher could not match to a backend route'
          : counts.state === 'no-analysis'
            ? 'Run analysis to populate the stitch review queue'
            : counts.state === 'no-call-graphs'
              ? 'Re-run analysis — the stored E2E diagram is missing call graphs'
              : hasWork
                ? `${counts.unresolved} unresolved · ${counts.ambiguous} ambiguous`
                : 'All call sites resolved'}
      </p>
    </Link>
  );
}

export function StitchReviewBadge({ projectId }: { projectId: string | undefined }) {
  const counts = useStitchCounts(projectId);
  if (!projectId || !counts) return null;
  const total = counts.unresolved + counts.ambiguous;
  if (total === 0) return null;

  return (
    <Link
      to={`/projects/${projectId}/stitch-review`}
      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded-lg transition-colors border border-amber-200 dark:border-amber-700"
      title={`${counts.unresolved} unresolved, ${counts.ambiguous} ambiguous stitches`}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
      {total} stitch{total === 1 ? '' : 'es'} to review
    </Link>
  );
}
